/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Fedorov Maxim
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @version 1.20.1
 * @since 1.0.1
 */

import { FC, Fragment } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { LinkEllipseBlock } from '../../../../../../components/common';
import { dropTableFilterAndSort, setTableFilter } from '../../../../../../components/Table/actions/tableActions';
import {
    IDropTableFilterAndSortPayload,
    ISetTableFilterPayload,
} from '../../../../../../components/Table/types/tableTypes';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { makeSelectLocalStorageSelectedAccount } from '../../../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import { RoutePath } from '../../../../../../routes/constants/routesConstants';
import {
    WidgetItemLeftContainerStyled,
    WidgetItemRightContainerStyled,
    WidgetListItem,
} from '../../styled/WidgetStyled';
import {
    EWidgetSearchAccountsField,
    IWidgetSearchAccountsItem,
} from '../../WidgetSearchAccounts/types/widgetSearchAccountsTypes';
import { EWidgetSubscriberMobile, IWidgetSubscriberMobileItem } from '../widgetSubscriberMobileTypes';

interface IWidgetSubscriberMobileListItem {
    field: string;
    items: IWidgetSubscriberMobileItem[];
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    type: EWidgetSubscriberMobile;
}

interface IDispatchProps {
    dropFilters: (payload: IDropTableFilterAndSortPayload) => void;
    setTableFilter: (payload: ISetTableFilterPayload) => void;
}

export type TWidgetSubscriberMobileListItem = IWidgetSubscriberMobileListItem & IConnectedProps & IDispatchProps;

export const WidgetSubscriberMobileListItem: FC<TWidgetSubscriberMobileListItem> = props => {
    const { items, field, type } = props;

    const setMobileLabel = (item: IWidgetSubscriberMobileItem): void => {
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetSubscriberMobile
                .onClickTabGroupWidgetSubscriberMobile(item.name, type),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    const getUrlToSubscribers = ({ id, name }: IWidgetSubscriberMobileItem): string => {
        const filters: any = {
            [field]: [{ value: id, label: name }],
        };

        if (props.selectedAccount[EWidgetSearchAccountsField.number]) {
            filters.account = [{
                value: props.selectedAccount[EWidgetSearchAccountsField.number],
                label: props.selectedAccount[EWidgetSearchAccountsField.number],
            }];
        }
        const filter: string = encodeURIComponent(JSON.stringify(filters));
        return `${RoutePath.subscriberMobile}?f=${filter}`;
    };

    return (
        <Fragment>
            {
                items.map((subscriberMobileItem: IWidgetSubscriberMobileItem) =>
                    <WidgetListItem key={subscriberMobileItem.id}>
                        <WidgetItemLeftContainerStyled>
                            <LinkEllipseBlock
                                dataName="setMobileLabel"
                                title={subscriberMobileItem.name}
                                url={getUrlToSubscribers(subscriberMobileItem)}
                                onClick={() => setMobileLabel(subscriberMobileItem)}
                            >
                                {subscriberMobileItem.name}
                            </LinkEllipseBlock>
                        </WidgetItemLeftContainerStyled>
                        <WidgetItemRightContainerStyled>
                            {subscriberMobileItem.count}
                        </WidgetItemRightContainerStyled>
                    </WidgetListItem>,
                )
            }
        </Fragment>
    );
};

interface IConnectedProps {
    selectedAccount: IWidgetSearchAccountsItem;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    selectedAccount: makeSelectLocalStorageSelectedAccount(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    dropFilters: (payload) => dispatch(dropTableFilterAndSort(payload)),
    setTableFilter: (payload: ISetTableFilterPayload) => dispatch(setTableFilter(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WidgetSubscriberMobileListItem);
